
import React from "react";
import { FlowRender } from "../flow-render";
import { AllNames } from "../onboarding-tda/shared-prms";
import { Text, dollarValidator } from "../prm/text";
import { Name } from "../prm/name";
import { RadioButton } from "../prm/radio-button";
import { OptionMenu } from "../prm/option-menu";
import { render_recurring_payment } from "./widgets/recurring-payment";
import {PaymentRequest, setContributionRequest} from "../../client/payment/actions";
import {price_string_to_cents, price_string_to_dollars} from "../currency-utils";
import { UserAcctType} from "../dwolla/constants";
import { Spinner } from "../../client/funcs/utils";
import {PaymentSource} from "../../client/payment/payment-source-js";
import { deep_copy } from "../js-utils";
import {sprintf} from "sprintf-js";
import {GoLiveController} from "./go-live-controller";
import {objToUrlParam} from "../url-util";
import {CustodianStatus} from "../constants";

const invest_start_amt = new Text(
  new Name(AllNames.invest_start_amt,
    "How much money would you like to start investing with?"));
invest_start_amt.setValidator(dollarValidator);
const ACH_LIMIT_USD = 50000;

const contribute_yes_no = new RadioButton(
  new Name(AllNames.contribute_yes_no,
    "Do you want to contribute on a regular basis?"),
  [
    new Name("yes", "Yes"),
    new Name("no", "No"),
  ]
);

// These match Cadence num in @ca/time
const contribute_cadence = new OptionMenu(
  new Name(AllNames.contribute_cadence, "How often"),
  [
    new Name("MONTHLY", "Monthly"),
    new Name("QUARTERLY", "Quarterly"),
    new Name("ANNUALLY", "Annually"),
  ]
);

const contribute_amt = new Text(
  new Name(AllNames.contribute_amt, "How much"));
contribute_amt.setValidator(dollarValidator);

const CD_EXPLORER_CONFIG = {
  suppress_contribution: true,
  purchase_fi: true,
  deposit_text: "You indicated you would like to purchase this amount of CDs.",
  next_url: {
    path: "/user/offerings",
    args: {
      acct_index: "__ACCT_INDEX__",
      table: "NON_CALLABLE_CDS",
    }
  },
  go_live_on_payment: true,
}


class Payment extends FlowRender {

  /**
   * config:
   * {
   *   suppress_contribution: boolean
   *   deposit_text: string,
   * }
   */
  constructor(config) {
    super(null, [
      invest_start_amt,
      contribute_yes_no,
      contribute_cadence,
      contribute_amt,
    ]);

    invest_start_amt.onChangeCallback = this.onChange;
    contribute_yes_no.onChangeCallback = this.onChange;
    contribute_cadence.onChangeCallback = this.onChange;
    contribute_amt.onChangeCallback = this.onChange;
    this.url = "payment";
    this.config = config || {};
  }

  init(componentState, appState, component) {
    super.init(componentState, appState, component);
    componentState.loading = false;
    componentState.error = "";
    componentState.zero_dialog = false;

    const onboarding = component.getOnboardingData();
    if (onboarding.purchase_item && onboarding.purchase_item.amt) {
      invest_start_amt.setValue(componentState, onboarding.purchase_item.amt);
    }

    const wlf = component.props.app.wlf;
    const do_cde = wlf.apps && wlf.apps.cd_explorer;
    if (do_cde) {
      this.config = CD_EXPLORER_CONFIG;
    }
  };

  canNavigateNext(component) {
    const index_token = "__ACCT_INDEX__";
    if (this.config.next_url) {
      let args = deep_copy(this.config.next_url.args);
      Object.keys(args).forEach(key => {
        if (args[key] === index_token) {
          args[key] = component.getOnboardingIndex();
        }
      })
      const onboarding = component.getOnboardingData();
      if (onboarding.purchase_item) {
        args.table = onboarding.purchase_item.table;
        args.amt_usd = onboarding.purchase_item.amt;
        args.cusip = onboarding.purchase_item.cusip;
      }
      return this.config.next_url.path + objToUrlParam(args);
    }
    return component.getRootUrl() + "/signing_done";
  }

  // Parse it from the url
  getFundingSourceId = component => {
    let funding_source_id = component.getPathQuery();
    if (funding_source_id && funding_source_id[0] === "?") {
      funding_source_id = funding_source_id.substring(1);
    }
    return funding_source_id;
  }

  onChange = (component, newState) => {
    newState.error = "";
  }

  renderSource = component => {
    const funding_source_id = this.getFundingSourceId(component);
    const dwolla_0 = component.getDwollaData(0);
    if (!(funding_source_id in dwolla_0.funding_source)) {
      console.log("Can't find", funding_source_id);
      return null;
    }

    const source = deep_copy(dwolla_0.funding_source[funding_source_id]);
    source.id = funding_source_id;

    return (
      <PaymentSource
        source={source}
        suppress_action_button={true}
        component={component}
        num_sources={1}/>);
  }

  setContribution = component => {
    const do_contrib = contribute_yes_no.getValue(component.state, true) === "yes";
    // If this were not an onboarding payment, we might have to call setContributionRequest with
    // do_contrib set to false to clear any previous contribution settings. But this is an
    // onboarding, there should be no pre-existing contribution settings to clear.
    if (do_contrib) {
      const amt_usd = price_string_to_dollars(contribute_amt.getValue(component.state));
      const cadence = contribute_cadence.getValue(component.state).id;
      const index = component.getOnboardingIndex();
      setContributionRequest(component.props.token, do_contrib, cadence, amt_usd, index)
        .then();
    }
  }

  paymentCB = component => () => {
    const amt_cents = price_string_to_cents(
      invest_start_amt.getValue(component.state));
    const is_segregated = component.props.wlf.payment && component.props.wlf.payment.segregated_payment;
    const tda_verified = component.getTDAData().status === CustodianStatus.VERIFIED;
    const username = component.props.user.username;
    const index = component.getOnboardingIndex();
    const acct_from = UserAcctType.EXTERNAL;
    let acct_to = sprintf("%04d", index);
    const is_funding_payment = true;
    let multi_leg = acct_to;

    if (!is_segregated && !tda_verified) {
      multi_leg = sprintf("%04d", index);
      acct_to = UserAcctType.BALANCE;
    }

    if (amt_cents <= 0.) {
      component.setState({
        error: "Amount must be greater than zero",
      });
      return;
    }
    if (amt_cents / 100 > ACH_LIMIT_USD) {
      component.setState({
        error: "ACH Limit is $50,000",
      });
      return;
    }
    if (this.config.purchase_fi) {
      if (amt_cents / 100. < 1000.) {
        component.setState({
          error: "Minimum of $1000 for CDs",
        })
        return;
      }
    }

    component.setState({ loading: true, error: "" });

    PaymentRequest(
      component.props.token, component.props.onboarding_token,
      username, acct_from, username, acct_to, amt_cents, null,
      multi_leg, is_funding_payment)
      .then(payment_resp => {
        component.props.dwollaUpdate(payment_resp.sender_dwolla, 0);
        if (index !== 0) {
          component.props.dwollaUpdate(payment_resp.receiver_dwolla, index);
        }
        component.props.addUserPaymentItem(username, payment_resp.payment_item);
        if (this.config.go_live_on_payment) {
          const ctrl = new GoLiveController(component);
          ctrl.goLive();
        }
      })
      .then(() => {
        component.setState({ loading: false });
        this.setContribution(component);
      })
      .catch(error => {
        console.log("Error", error);
        component.setState({ loading: false, error: "There was an error"});
      });

  }

  maybeRenderContribution = component => {
    if (this.config.suppress_contribution) {
      return null;
    }

    return (
      <div>
        <div>{contribute_yes_no.name.label}</div>
        <div style={{height: "0.5rem"}}/>
        { render_recurring_payment(
          component, contribute_yes_no, contribute_amt, contribute_cadence) }
      </div>
    )
  }

  renderDepositAmt = component => {
    const input_style = {
      width: "100%",
      textAlign: "center",
    };
    const deposit_text = this.config.deposit_text || invest_start_amt.name.label;
    const error_style = {
      color: "#F66",
      fontWeight: "bold",
      height: "1rem",
    }
    return (
      <div>
        <div style={{marginBottom: "0.5rem"}}>{deposit_text}</div>
        <div style={{width: "20rem"}}>
          <div style={{ display: "flex", alignItems: "center"}}>
            <div>$</div>
            <div style={{width: "0.5rem"}}/>
            <div style={{flex: "1"}}>
              { invest_start_amt.renderInput(component, input_style) }
            </div>
          </div>
          <div align={"center"} style={error_style}>
            { component.state.error || " " }
          </div>
        </div>
      </div>
    )
  }

  renderBody = component => {
    const button_text = component.state.loading
      ? Spinner() : "Initiate Deposit";
    // We are recording payments, we use dwolla_NNNN
    const dwolla = component.getDwollaData();
    let did_payment = Object.keys(dwolla.payments).length > 0;
    const did_payment_elem = (
      <div align={"center"}>
        <div style={{fontSize: "1.5rem"}}>Account funded. Thank You!</div>
      </div>
    );

    return (
      <div>
        <div align={"center"}>
          <div style={{fontSize: "2.5rem"}}>Deposit</div>
        </div>
        { this.renderSource(component) }
        <div style={{height: "3rem"}} />
        { this.renderDepositAmt(component) }
        { this.maybeRenderContribution(component) }
        <div style={{height: "1rem"}} />
        { !did_payment && this.renderActionButton(
          button_text, this.paymentCB(component), component.state.loading)}
        { did_payment && did_payment_elem }
        <div align={"center"} style={{paddingTop: "1rem"}}>
          { did_payment && this.renderNextButton(component) }
        </div>
      </div>
    )
  }

  render = component => {
    return this.renderSingleColumn(this.renderBody(component));
  }
}

export { Payment };
